import { Box, styled } from '@mui/material';
import { LocaleContext } from 'new/i18n/LocaleContext';
import React, { useContext } from 'react';
import { LandingPageCommonProps } from '../../types';
import {
  commonLandingPageStyles,
  sectionLandingPageStyles,
} from '../../styles';
import { useIntl } from 'react-intl';
import { Plan } from 'new/modules/MembershipPlansManage/types';
import { formatNumberAsCurrency } from 'new/utils/numbers';

const Wrapper = styled(Box)(() => ({
  ...commonLandingPageStyles,
  ...sectionLandingPageStyles,
  display: 'flex',
  justifyContent: 'center',
  flexWrap: 'wrap',
  padding: '2rem 0 0',
  color: '#444',
  fontFamil: 'Lexend',

  '.plan_box-wrapper': {
    padding: '0 0 3.5rem 0',
    display: 'flex',
  },

  '.plan_box': {
    backgroundColor: 'white',
    position: 'relative',
    padding: '3rem 0 0 0',
    margin: '0 0.5rem',
    width: '100%',
    maxWidth: '300px',
    minWidth: '300px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    ul: {
      paddingLeft: '1rem',
    },

    li: {
      fontSize: '0.8rem',
      backgroundRepeat: 'no-repeat',
      marginTop: '5px',
    },
    '.plan_description': {
      padding: '15px',
      minHeight: '13.5rem',
    },

    '.plan_header': {
      fontWeight: 700,
      fontSize: '18px',
      paddingBottom: '0.5rem',
    },

    '.plan_disclaimer': {
      padding: '15px',
      lineHeight: '1rem',
      color: '#999',
      fontStyle: 'italic',
    },

    '.plan_prices': {
      color: 'white',
      backgroundColor: '#ccc',
      textAlign: 'center',
      height: '3rem',
      lineHeight: '3rem',
      position: 'absolute',
      bottom: '-40px',
      width: '100%',
    },
    '.plan_title_box': {
      textAlign: 'center',
      lineHeight: '1.2rem',
      position: 'absolute',
      top: 0,
      width: '100%',
      display: 'flex',
      flexFlow: 'wrap',
      flexDirection: 'column',
    },

    '.plan_title': {
      fontSize: '1.2rem',
      fontWeight: 'bold',
      '-webkit-font-smoothing': 'antialiased',
      padding: '1rem',
    },
    '.age_limits': {
      fontSize: '.6rem',
      display: 'block',
      lineHeight: '0.5rem',
      marginTop: '-0.8rem',
      paddingBottom: '0.3rem',
    },

    '.plan_img': {
      marginTop: '-4px',
    },

    '.plan-price': {
      paddingBottom: '1rem',

      sup: {
        fontSize: '50%',
        top: '-1em',
      },
    },

    '.top-price': {
      fontSize: '4rem',
      lineHeight: '4rem',
    },
    '.annual-price': {
      color: '#878787',
    },
    '.prices_disabled': {
      display: 'none',
    },
    '.plan-includes': {
      textTransform: 'uppercase',
      fontSize: '0.8rem',
      letterSpacing: '0.3rem',
      width: '70%',
      margin: '0 auto',
      display: 'flex',

      '&:before, &:after': {
        content: "''",
        flexGrow: 1,
        margin: '0px 7px 0 2px',
        background: 'linear-gradient(to right, #cccccc, #cccccc)',
        backgroundSize: '100% 1px',
        backgroundPosition: '0% 50%',
        backgroundRepeat: 'repeat-x',
      },
    },
  },
}));

const StyledBoxSaveAmount = styled(Box)({
  textAlign: 'center',
  fontSize: '1.5rem',
  fontWeight: 'bold',
});

export type PlansVisionProps = {
  memberships: Plan[];
  smBusinessInfo: {
    smbizBusinessInvites: {
      discountPercentage: number;
    };
  };
} & LandingPageCommonProps;

const PlansVision: React.FC<PlansVisionProps> = ({
  memberships,
  colorOneHex,
  colorOne,
  smBusinessInfo,
}) => {
  const intl = useIntl();
  const { locale } = useContext(LocaleContext);
  const isSpanish = locale === 'es-US';
  let count = 6;

  const discount =
    smBusinessInfo != null
      ? smBusinessInfo.smbizBusinessInvites.discountPercentage
      : 0;

  const customYearlyMemberships: Plan[] = [];
  memberships.map((m) => {
    if (
      (m.type == 'custom_year' || m.type == 'year') &&
      // m.active == true &&
      // m.showOnEnrollment == true &&
      m.monthlyId
    )
      customYearlyMemberships[m.monthlyId] = m;
  });

  const standardMemberships = memberships.filter(
    (m) =>
      (m.type == 'month' || m.type == 'custom') &&
      (m.showOnEnrollment == true ||
        (m.showOnEnrollment == false && Number(m.price) == 0) ||
        (m.showOnEnrollment == false &&
          customYearlyMemberships[m.id] != undefined &&
          customYearlyMemberships[m.id].showOnEnrollment == true)),
  );
  const onlyYearlyNoMonthlyMemberships = memberships.filter(
    (m) =>
      (m.type == 'custom_year' || m.type == 'year') &&
      (m.monthlyId == null ||
        customYearlyMemberships[m.monthlyId] == undefined),
  );

  const customMonthlyMemberships = standardMemberships.concat(
    onlyYearlyNoMonthlyMemberships,
  );

  customMonthlyMemberships.sort((a, b) => {
    const nameA = a.sortOrder;
    const nameB = b.sortOrder;
    if (nameA < nameB) {
      return -1;
    } else if (nameA > nameB) {
      return 1;
    }
    return 0;
  });
  const base = colorOne == 'blue' ? 204 : 0;

  return (
    <Wrapper>
      {customMonthlyMemberships.map((membership, index) => {
        let discounted_price =
          Math.round(Number(membership.price) * (100 - discount)) / 100;
        if (membership.showOnEnrollment == false) discounted_price = 0;
        const yearly_discounted =
          customYearlyMemberships[membership.id] == undefined
            ? 0
            : Math.round(
                Number(customYearlyMemberships[membership.id].price) *
                  (100 - discount),
              ) / 100;
        const backgroundColor = `hsl(${base}, 1%, ${Math.floor(
          92 - ((92 - 78) * index) / (customMonthlyMemberships.length - 1),
        )}%)`;
        count++;
        return (
          <div
            key={index}
            id={count.toString()}
            className="plan_box-wrapper"
            style={{ backgroundColor: 'veryltgrey' }}
          >
            <div className="plan_box" style={{ backgroundColor }}>
              <div className="plan_title_box">
                <div className="plan_title">
                  {isSpanish && membership.spanishName
                    ? membership.spanishName
                    : membership.name}
                </div>
              </div>
              <div className="plan_description">
                <ul>
                  {(
                    (isSpanish && membership?.spanishDescription
                      ? membership?.spanishDescription
                      : membership?.description) ?? ''
                  )
                    .split(/(\r\n|\n|\r)/gm)
                    .map((i) => i.replace(/(\r\n|\n|\r|- )/gm, ''))
                    .filter((i) => i)
                    .map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                </ul>
                {membership.saveAmount && (
                  <StyledBoxSaveAmount>
                    save ${membership.saveAmount}
                  </StyledBoxSaveAmount>
                )}
              </div>
              <div
                className="plan_prices"
                style={{ backgroundColor: colorOneHex }}
              >
                {discounted_price != 0 && (
                  <span>
                    <b>
                      $
                      {formatNumberAsCurrency(
                        discounted_price,
                        2,
                        false,
                        true,
                      ) +
                        '/' +
                        intl.formatMessage({ id: 'label.mo' })}
                      {membership.activationFee ? '*' : ''}
                    </b>
                  </span>
                )}
                {customYearlyMemberships[membership.id] != undefined &&
                  customYearlyMemberships[membership.id].viewStatus ==
                    'active' && (
                    <span
                      className={
                        customYearlyMemberships[membership.id].viewStatus !=
                          'active' ||
                        customYearlyMemberships[membership.id]
                          .showOnEnrollment == false
                          ? 'prices_disabled'
                          : ''
                      }
                    >
                      {' '}
                      {discounted_price != 0 &&
                        intl.formatMessage({ id: 'label.or' })}{' '}
                      <b>
                        $
                        {formatNumberAsCurrency(
                          yearly_discounted,
                          2,
                          false,
                          true,
                        ) +
                          '/' +
                          intl.formatMessage({ id: 'label.yr' })}
                      </b>
                    </span>
                  )}
              </div>
            </div>
          </div>
        );
      })}
    </Wrapper>
  );
};

export default PlansVision;
